import { getItem, setItem } from '../../helpers/webStorage';
import type { AddToCartData, CartEntryData, SelectedVariant } from '../../types';
import {
  ORDER_IDS,
  Product,
  Products,
  PurchaseActionField,
  PurchaseCustomDimensions,
  PurchaseEvent,
  RolexProduct,
  pageCategoryType
} from '../types';

// eslint-disable-next-line import/prefer-default-export
export const mapRolexProductDetailsToAnalyticsProduct = (): RolexProduct => {
  const {
    nameWithBrand,
    productCode,
    manufacturer,
    skuForDataLayer,
    skuCategory,
    priceUponEnquiry,
    attraqt: { fhProductCode },
    supplierCode,
    analyticsId
  } = window.globalProductDetails;

  return {
    id: productCode,
    fhProductCode,
    brand: manufacturer,
    name: nameWithBrand,
    variant: '',
    category: skuCategory,
    sku: skuForDataLayer,
    priceUponEnquiry: priceUponEnquiry ? 'Y' : 'N',
    mpn: supplierCode,
    analyticsId
  };
};

export const mapProductDetailsToAnalyticsProduct = (): Product => {
  const {
    nameWithBrand,
    productCode,
    manufacturer,
    basePrice,
    skuForDataLayer,
    skuCategory,
    priceUponEnquiry,
    attraqt: { fhProductCode }
  } = window.globalProductDetails;

  return {
    id: productCode,
    fhProductCode,
    brand: manufacturer,
    name: nameWithBrand,
    variant: '',
    price: basePrice,
    category: skuCategory,
    sku: skuForDataLayer,
    priceUponEnquiry: priceUponEnquiry ? 'Y' : 'N'
  };
};

export const mapSelectedVariantToAnalyticsProduct = (selectedVariant: SelectedVariant): Product => {
  const {
    nameWithBrand,
    baseProductName,
    productCode,
    manufacturer,
    price,
    skuForDataLayer,
    fhProductCode,
    skuCategory,
    productVariant
  } = selectedVariant;

  return {
    id: productCode,
    fhProductCode,
    brand: manufacturer,
    name: nameWithBrand || baseProductName,
    variant: productVariant,
    category: skuCategory,
    price,
    sku: skuForDataLayer
  };
};

export const mapAddToCartDataToAnalyticsProduct = ({
  entry: {
    productCode,
    fhProductCode,
    price,
    nameWithBrand,
    baseProductName,
    manufacturer,
    productVariant,
    skuForDataLayer,
    skuCategory
  }
}: AddToCartData): Product => ({
  id: productCode,
  fhProductCode,
  brand: manufacturer,
  name: nameWithBrand || baseProductName,
  variant: productVariant,
  category: skuCategory,
  quantity: 1,
  price,
  sku: skuForDataLayer
});

export const mapCartEntryToAnalyticsProducts = ({
  nameWithBrand,
  baseProductName,
  productCode,
  fhProductCode,
  skuForDataLayer,
  skuCategory,
  manufacturer,
  quantity,
  basePrice,
  productVariant
}: CartEntryData): Product => ({
  id: productCode,
  fhProductCode,
  brand: manufacturer,
  name: nameWithBrand || baseProductName,
  variant: productVariant,
  quantity,
  price: basePrice.value || 0,
  category: skuCategory,
  sku: skuForDataLayer
});

export const mapCartEntriesToAnalyticsProducts = (cartEntries: CartEntryData[]): Products =>
  cartEntries?.map(mapCartEntryToAnalyticsProducts);

export const mapOrderConfirmationToPurchaseActionField = (): PurchaseActionField => {
  const { orderId, revenue, shipping, tax, voucherCode } = window.globalOrderConfirmation;

  return {
    id: orderId,
    affiliation: 'Loupe',
    revenue,
    tax,
    shipping,
    coupon: voucherCode
  };
};

export const mapPurchasedProductsToAnalyticsProducts = (): Products => {
  const { entries } = window.globalOrderConfirmation;
  return entries?.map(
    ({
      nameWithBrand,
      baseProductName,
      manufacturer,
      productVariant,
      productCode,
      quantity,
      price,
      priceType,
      promotions,
      skuForDataLayer,
      skuCategory,
      attraqt: { fhProductCode },
      deliveryTime
    }) => ({
      id: productCode,
      fhProductCode,
      brand: manufacturer,
      name: nameWithBrand || baseProductName,
      variant: productVariant,
      category: skuCategory,
      price,
      quantity,
      priceType,
      promotions,
      sku: skuForDataLayer,
      deliveryTime
    })
  );
};

export const mapOrderConfirmationToPurchaseCustomDimensions = (): PurchaseCustomDimensions => {
  const { paymentType, userType, deliveryMethod, ifcTerm, vee24Usage } = window.globalOrderConfirmation;

  return {
    paymentType, // dimension 1
    userType, // dimension 6
    deliveryMethod, // dimension 8
    ifcTerm, // dimension 9
    vee24Usage // dimension 14
  };
};

export const mapOrderConfirmationToPurchaseEvent = (): PurchaseEvent => {
  const actionField = mapOrderConfirmationToPurchaseActionField();
  const products = mapPurchasedProductsToAnalyticsProducts();
  const dimensions = mapOrderConfirmationToPurchaseCustomDimensions();

  return {
    ecommerce: {
      purchase: {
        actionField,
        products
      }
    },
    ...dimensions
  };
};

export const mapPageCategoriesToEpsilonEvent = (): pageCategoryType | null =>
  window.bbGlobal.category.type === ''
    ? null
    : {
        ecommerce: {
          categoryType: window.bbGlobal.category.type,
          categoryName: window.bbGlobal.category.name
        }
      };

export const isAlreadyTracked = (): boolean => {
  const { isAlreadyTracked: isAlreadyTrackedOnServerSide, orderId } = window.globalOrderConfirmation;
  const orderIds: string[] = getItem(ORDER_IDS, 'localStorage') || [];

  if (isAlreadyTrackedOnServerSide || orderIds.includes(orderId)) return true;

  setItem(ORDER_IDS, [...orderIds, orderId], 'localStorage');
  return false;
};
